/**
 * smcEngine.ts
 *
 * Smart Money Concepts engine — orchestration entrypoint.
 * Submodule logic extracted to: structure.ts, fvg.ts, displacement.ts,
 * orderBlocks.ts, sweeps.ts, scoring.ts, types.ts
 *
 * Philosophy:
 * - Only surface HIGH-VALUE, HIGH-CONFIDENCE signals
 * - Distinguish major vs minor structure
 * - Feed outputs into regime, trap, and setup engines
 * - No chart clutter — rank by recency and significance
 */

import {
  Candle, AppSettings, SMCResult,
  FairValueGap,
  ScenarioType, LiquiditySide,
} from '../../../../types';
import { SMCThresholds } from './types';
import { detectSwingHighs, detectSwingLows, detectStructureEvents } from './structure';
import { detectFVGs } from './fvg';
import { detectDisplacements } from './displacement';
import { detectOrderBlocks } from './orderBlocks';
import { detectLiquiditySweeps } from './sweeps';
import { analyzePremiumDiscount, determineBias, calcSMCConfidence } from './scoring';

// ─── Sensitivity thresholds ───────────────────────────────────────────────────

function getThresholds(sensitivity: AppSettings['smcSensitivity']): SMCThresholds {
  if (sensitivity === 'high') return {
    swingLookback: 3,  fvgMinSize: 0.12, displacementMinBody: 0.30,
    displacementMinVol: 1.3, sweepLookback: 25, maxFVGs: 5, maxOBs: 3, maxStructureEvents: 6
  };
  if (sensitivity === 'medium') return {
    swingLookback: 5,  fvgMinSize: 0.18, displacementMinBody: 0.50,
    displacementMinVol: 1.6, sweepLookback: 20, maxFVGs: 4, maxOBs: 2, maxStructureEvents: 5
  };
  return {
    swingLookback: 7,  fvgMinSize: 0.25, displacementMinBody: 0.65,
    displacementMinVol: 1.8, sweepLookback: 15, maxFVGs: 3, maxOBs: 2, maxStructureEvents: 4
  };
}

// ─── Main exported function ───────────────────────────────────────────────────

export function analyzeSMC(candles: Candle[], settings: AppSettings): SMCResult | null {
  if (!candles || candles.length < 15) return null;

  const thresholds = getThresholds(settings.smcSensitivity);

  // Core detections
  const swingHighs      = detectSwingHighs(candles, thresholds.swingLookback);
  const swingLows       = detectSwingLows(candles, thresholds.swingLookback);
  const displacements   = detectDisplacements(candles, thresholds);
  const structureEvents = detectStructureEvents(candles, swingHighs, swingLows, thresholds);
  const fvgs            = detectFVGs(candles, thresholds);
  const obs             = detectOrderBlocks(candles, displacements, thresholds);
  const sweeps          = detectLiquiditySweeps(candles, swingHighs, swingLows, thresholds);
  const premiumDiscount = analyzePremiumDiscount(candles, swingHighs, swingLows);

  const curPrice = candles[candles.length - 1].close;

  // Classify
  const { bias, strength: biasStrength } = determineBias(swingHighs, swingLows, structureEvents, candles);

  const lastBOS   = structureEvents.find(e => e.type === 'BOS') || null;
  const lastCHOCH = structureEvents.find(e => e.type === 'CHOCH') || null;

  const fvgAbove = fvgs.filter(f => f.dir === 'bearish' && f.bottom > curPrice)
    .sort((a, b) => a.bottom - b.bottom)[0] || null;
  const fvgBelow = fvgs.filter(f => f.dir === 'bullish' && f.top < curPrice)
    .sort((a, b) => b.top - a.top)[0] || null;

  const obAbove = obs.filter(o => o.type === 'bearish' && o.bottom > curPrice)
    .sort((a, b) => a.bottom - b.bottom)[0] || null;
  const obBelow = obs.filter(o => o.type === 'bullish' && o.top < curPrice)
    .sort((a, b) => b.top - a.top)[0] || null;

  const recentSweep        = sweeps[0] || null;
  const recentDisplacement = displacements[0] || null;

  const _sweepReclaimed = !!(recentSweep?.reclaimed && recentSweep.candleIdx >= candles.length - 4);
  const _sweepDir       = recentSweep?.type === 'sweep_low' ? 'bullish' : 'bearish';
  const _reversalDisp   = displacements.some(d =>
    d.dir === _sweepDir &&
    recentSweep !== null &&
    d.candleIdx > recentSweep.candleIdx &&
    d.candleIdx <= candles.length - 1
  );
  const sweepAndReverse = _sweepReclaimed && _reversalDisp;

  const displacementAligned = !!(recentDisplacement && bias !== 'neutral' &&
    recentDisplacement.dir === (bias === 'bullish' ? 'bullish' : 'bearish'));

  const confidenceScore = calcSMCConfidence(
    bias, biasStrength, lastBOS, lastCHOCH, recentSweep, recentDisplacement,
    fvgBelow, fvgAbove, obBelow, obAbove, premiumDiscount, candles
  );

  // Build summary and key signal
  const biasLabel    = bias === 'neutral' ? 'Neutral' : bias === 'bullish' ? 'Bullish' : 'Bearish';
  const strengthLabel = biasStrength === 'major' ? 'strong' : 'weak';

  let keySignal = 'No dominant SMC signal';
  if (sweepAndReverse) {
    keySignal = `${recentSweep!.type === 'sweep_low' ? 'Bullish' : 'Bearish'} sweep + reclaim — reversal candidate (displacement confirmed)`;
  } else if (lastCHOCH && lastCHOCH.candleIdx >= candles.length - 10) {
    keySignal = `${lastCHOCH.dir === 'bullish' ? 'Bullish' : 'Bearish'} context shift candidate — CHOCH at ${lastCHOCH.price.toFixed(2)}, needs confirmation`;
  } else if (recentDisplacement && displacementAligned && recentDisplacement.candleIdx >= candles.length - 5) {
    keySignal = `${recentDisplacement.dir === 'bullish' ? 'Bullish' : 'Bearish'} displacement aligned with ${bias} bias — momentum continuation`;
  } else if (fvgBelow && (curPrice - fvgBelow.top) / curPrice < 0.008) {
    keySignal = `Approaching bullish FVG at ${fvgBelow.bottom.toFixed(2)}–${fvgBelow.top.toFixed(2)} — potential demand zone`;
  } else if (fvgAbove && (fvgAbove.bottom - curPrice) / curPrice < 0.008) {
    keySignal = `Approaching bearish FVG at ${fvgAbove.bottom.toFixed(2)}–${fvgAbove.top.toFixed(2)} — potential supply zone`;
  } else if (obBelow && (curPrice - obBelow.top) / curPrice < 0.010) {
    keySignal = `At bullish OB (${obBelow.bottom.toFixed(2)}–${obBelow.top.toFixed(2)}) — institutional demand zone`;
  } else if (lastBOS && lastBOS.candleIdx >= candles.length - 8) {
    keySignal = `Recent ${lastBOS.dir === 'bullish' ? 'bullish' : 'bearish'} BOS — structure confirms ${lastBOS.dir} continuation bias`;
  }

  const summary = `${biasLabel} ${strengthLabel} bias${premiumDiscount ? ` | ${premiumDiscount.zone} zone` : ''}${lastCHOCH ? ' | CHOCH active' : lastBOS ? ' | BOS confirmed' : ''}`;

  // ── Displacement quality model ───────────────────────────────────────────
  const dispDir: 'bullish' | 'bearish' | 'none' =
    recentDisplacement ? recentDisplacement.dir : 'none';
  const dispStrength = recentDisplacement
    ? Math.min(1, recentDisplacement.size / 2.0)
    : 0;
  const dispAge = recentDisplacement ? (candles.length - 1 - recentDisplacement.candleIdx) : 999;
  const dispFreshness = recentDisplacement ? Math.max(0, 1 - dispAge / 10) : 0;
  const dispBreaksStructure = !!(recentDisplacement && (lastBOS || lastCHOCH) &&
    structureEvents.some(e => Math.abs(e.candleIdx - recentDisplacement.candleIdx) <= 3));
  const dispCreatesImbalance = !!(recentDisplacement && (
    (recentDisplacement.dir === 'bullish' && fvgBelow && fvgBelow.candleIdx >= recentDisplacement.candleIdx - 2) ||
    (recentDisplacement.dir === 'bearish' && fvgAbove && fvgAbove.candleIdx >= recentDisplacement.candleIdx - 2)
  ));

  // Event volume score
  const recentBars = candles.slice(-Math.min(5, candles.length));
  const allBars    = candles.slice(-Math.min(20, candles.length));
  const recentAvgVol = allBars.reduce((s, c) => s + c.volume, 0) / allBars.length;
  const maxRecentBarVol = recentBars.reduce((mx, c) => Math.max(mx, c.volume), 0);
  const eventVolumeScore = recentAvgVol > 0
    ? Math.min(1, maxRecentBarVol / (recentAvgVol * 3))
    : 0;
  const institutionalEventVolume = eventVolumeScore >= 0.5 &&
    !!(recentDisplacement && recentDisplacement.volumeMultiple >= 1.5);

  // ── Liquidity sequencing ─────────────────────────────────────────────────
  let lastLiquidityTaken: LiquiditySide | null = null;
  let expectedNextLiquidity: LiquiditySide | null = null;
  let liquiditySequenceConfidence = 0;

  if (recentSweep) {
    if (recentSweep.type === 'sweep_low') {
      lastLiquidityTaken = 'sell_side';
      if (recentDisplacement?.dir === 'bullish') {
        expectedNextLiquidity = 'buy_side';
        liquiditySequenceConfidence = Math.min(1, dispStrength * 0.6 + dispFreshness * 0.4);
      }
    } else {
      lastLiquidityTaken = 'buy_side';
      if (recentDisplacement?.dir === 'bearish') {
        expectedNextLiquidity = 'sell_side';
        liquiditySequenceConfidence = Math.min(1, dispStrength * 0.6 + dispFreshness * 0.4);
      }
    }
  }

  // ── Scenario classification ──────────────────────────────────────────────
  let scenarioType: ScenarioType = 'none';
  if (recentSweep && recentDisplacement) {
    const sweepDir = recentSweep.type === 'sweep_low' ? 'bullish' : 'bearish';
    if (sweepAndReverse && recentSweep.reclaimed) {
      scenarioType = 'reversal';
    } else if (recentDisplacement.dir === sweepDir && dispFreshness > 0.3) {
      scenarioType = 'continuation';
    } else {
      scenarioType = 'transition';
    }
  } else if (recentDisplacement && dispFreshness > 0.5 && (lastBOS || lastCHOCH)) {
    scenarioType = dispBreaksStructure ? 'continuation' : 'transition';
  }

  // ── Best FVG selection ───────────────────────────────────────────────────
  const fvgQuality = (f: FairValueGap, dir: 'bullish' | 'bearish'): number => {
    type FVGExt = FairValueGap & { fvgWeight?: number; fvgAge?: number };
    const ext = f as FVGExt;
    const weight    = ext.fvgWeight ?? 0.5;
    const proximity = 1 - Math.min(1, Math.abs(
      dir === 'bullish'
        ? (curPrice - f.top) / curPrice
        : (f.bottom - curPrice) / curPrice
    ) / 0.01);
    const dirAlign  = f.dir === dir ? 1 : 0;
    const pdAlign   = premiumDiscount
      ? (dir === 'bullish' && premiumDiscount.zone === 'discount' ? 1
        : dir === 'bearish' && premiumDiscount.zone === 'premium' ? 1
        : 0.4)
      : 0.5;
    return weight * 0.35 + proximity * 0.35 + dirAlign * 0.2 + pdAlign * 0.1;
  };

  const biasDir = bias === 'bullish' ? 'bullish' : 'bearish';
  const allScoredFVGs = fvgs.map(f => ({ fvg: f, score: fvgQuality(f, biasDir) }));
  allScoredFVGs.sort((a, b) => b.score - a.score);
  const bestFVG = allScoredFVGs[0]?.fvg ?? null;

  return {
    bias, biasStrength, structureEvents,
    lastBOS, lastCHOCH,
    fvgAbove, fvgBelow, allFVGs: fvgs,
    obAbove, obBelow, allOBs: obs,
    recentSweep, sweepAndReverse,
    recentDisplacement, displacementAligned,
    premiumDiscount, confidenceScore, summary, keySignal,
    displacementDirection: dispDir,
    displacementStrength:  dispStrength,
    displacementFreshness: dispFreshness,
    displacementBreaksStructure: dispBreaksStructure,
    displacementCreatesImbalance: dispCreatesImbalance,
    institutionalEventVolume,
    eventVolumeScore,
    lastLiquidityTaken,
    expectedNextLiquidity,
    liquiditySequenceConfidence,
    scenarioType,
    bestFVG,
  };
}
